import { RotateCcw, Search } from "lucide-react";

import { DateRangePicker } from "@/components/shared/date-range-picker";
import { SearchableSelect } from "@/components/shared/searchable-select";
import { Button } from "@/components/ui/button";
import { CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMe } from "@/hooks/use-auth";
import { useWorkspaces } from "@/hooks/use-workspaces";

import { ACTION_OPTIONS, ENTITY_OPTIONS } from "./activity-log-schema";

interface ActivityLogFiltersProps {
  q: string;
  setQ: (value: string) => void;
  dateFrom: string;
  dateTo: string;
  setDateRange: (from: string, to: string) => void;
  entityName: string;
  setEntityName: (value: string) => void;
  activity: string;
  setActivity: (value: string) => void;
  workspaceId: string;
  setWorkspaceId: (value: string) => void;
  resetFilters: () => void;
}

export function ActivityLogFilters({
  q,
  setQ,
  dateFrom,
  dateTo,
  setDateRange,
  entityName,
  setEntityName,
  activity,
  setActivity,
  workspaceId,
  setWorkspaceId,
  resetFilters,
}: ActivityLogFiltersProps) {
  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = (workspacesData?.items ?? []).filter((w) => w.isActive);
  return (
    <CardHeader className="pb-4">
      <CardTitle className="text-base font-medium">Filter Log</CardTitle>
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cari pengguna, entitas, ID..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9 w-full"
          />
        </div>

        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={setDateRange}
          className="h-9 min-w-[240px]"
        />

        <Select value={entityName} onValueChange={setEntityName}>
          <SelectTrigger className="h-9 w-44">
            <SelectValue placeholder="Semua Modul" />
          </SelectTrigger>
          <SelectContent>
            {ENTITY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={activity} onValueChange={setActivity}>
          <SelectTrigger className="h-9 w-40">
            <SelectValue placeholder="Semua Aksi" />
          </SelectTrigger>
          <SelectContent>
            {ACTION_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {me?.canSwitchWorkspace && (
          <div className="h-9 w-44">
            <SearchableSelect
              value={workspaceId}
              onChange={setWorkspaceId}
              options={workspaces.map((w) => ({ value: w.id, label: w.name }))}
              placeholder="Semua Workspace"
            />
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={resetFilters}
          className="h-9"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
          Reset
        </Button>
      </div>
    </CardHeader>
  );
}
